import React, { useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  Button,
  Chip,
  Avatar,
  TextField,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
} from '@mui/material';
import {
  Close as CloseIcon,
  DeleteOutline as DeleteOutlineIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  ShoppingBagOutlined as ShoppingBagIcon,
  Payment as CheckoutIcon,
} from '@mui/icons-material';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { OrderFormData, CartItem } from '../types/cart';
import orderService from '../services/orderService';
import DatePickerDialog from './DatePickerDialog';
import { CalendarToday as CalendarIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import { CHAT_CHA as C } from '../theme/chatChaavi';

interface VendorOrderSummary {
  phone: string;
  vendorName: string;
  total: number;
  min: number;
  businessNames: string[];
}

interface CartProps {
  open: boolean;
  onClose: () => void;
  onCheckout?: () => void;
}

const Cart: React.FC<CartProps> = ({ open, onClose, onCheckout }) => {
  const cartContext = useCart();
  const { cart, removeFromCart, updateQuantity, updateBookingDate, clearCart } = cartContext;
  const { user } = useAuth();

  const [checkoutDialogOpen, setCheckoutDialogOpen] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [selectedItemForDate, setSelectedItemForDate] = useState<{
    id: string;
    type: 'theme' | 'inventory' | 'plate' | 'dish';
  } | null>(null);
  const [orderForm, setOrderForm] = useState<OrderFormData>({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    deliveryAddress: '',
    deliveryDate: '',
    specialNotes: '',
  });
  const [orderLoading, setOrderLoading] = useState(false);
  const [orderError, setOrderError] = useState<string | null>(null);

  const formatPrice = React.useCallback((price: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(price);
  }, []);

  const vendorSummaries = React.useMemo<VendorOrderSummary[]>(() => {
    const summaryMap = new Map<string, VendorOrderSummary>();

    cart.items.forEach(item => {
      if (!item.vendorPhone) {
        return;
      }

      if (!summaryMap.has(item.vendorPhone)) {
        summaryMap.set(item.vendorPhone, {
          phone: item.vendorPhone,
          vendorName: item.businessName,
          total: 0,
          min: item.vendorMinOrderAmount ?? 0,
          businessNames: [],
        });
      }

      const summary = summaryMap.get(item.vendorPhone)!;
      summary.total += item.price * item.quantity;
      if (typeof item.vendorMinOrderAmount === 'number') {
        summary.min = item.vendorMinOrderAmount;
      }
      if (!summary.businessNames.includes(item.businessName)) {
        summary.businessNames.push(item.businessName);
      }
    });

    return Array.from(summaryMap.values());
  }, [cart.items]);

  const vendorsBelowMinimum = React.useMemo(
    () => vendorSummaries.filter(summary => summary.min > 0 && summary.total + 0.0001 < summary.min),
    [vendorSummaries]
  );

  const vendorMinimumError = React.useMemo(() => {
    if (vendorsBelowMinimum.length === 0) {
      return null;
    }

    return vendorsBelowMinimum
      .map(summary => {
        const shortfall = summary.min - summary.total;
        return `${summary.vendorName} requires ${formatPrice(summary.min)} (add ${formatPrice(shortfall)})`;
      })
      .join(' • ');
  }, [vendorsBelowMinimum, formatPrice]);

  const canProceedToCheckout = vendorsBelowMinimum.length === 0;

  const handleQuantityChange = (
    itemId: string,
    itemType: 'theme' | 'inventory' | 'plate' | 'dish',
    newQuantity: number
  ) => {
    if (newQuantity <= 0) {
      removeFromCart(itemId, itemType);
    } else {
      updateQuantity(itemId, itemType, newQuantity);
    }
  };

  const handleDatePickerOpen = (itemId: string, itemType: 'theme' | 'inventory' | 'plate' | 'dish') => {
    setSelectedItemForDate({ id: itemId, type: itemType });
    setDatePickerOpen(true);
  };

  const handleDateConfirm = (date: string | undefined) => {
    if (selectedItemForDate) {
      updateBookingDate(selectedItemForDate.id, selectedItemForDate.type, date);
    }
    setDatePickerOpen(false);
    setSelectedItemForDate(null);
  };

  const handleCheckout = () => {
    if (!canProceedToCheckout) {
      return;
    }

    const userFullName =
      user?.firstName && user?.lastName
        ? `${user.firstName} ${user.lastName}`.trim()
        : user?.firstName || user?.lastName || '';

    let earliestBookingDate = '';
    if (cart.items.length > 0) {
      const bookingDates = cart.items
        .map(item => item.bookingDate)
        .filter((date): date is string => date !== undefined && date !== null && date !== '');

      if (bookingDates.length > 0) {
        bookingDates.sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
        earliestBookingDate = bookingDates[0];
      } else {
        const today = new Date();
        earliestBookingDate = today.toISOString().split('T')[0];
      }
    } else {
      const today = new Date();
      earliestBookingDate = today.toISOString().split('T')[0];
    }

    setOrderForm({
      customerName: userFullName,
      customerEmail: user?.email || '',
      customerPhone: user?.phoneNumber || '',
      deliveryAddress: orderForm.deliveryAddress || '',
      deliveryDate: earliestBookingDate,
      specialNotes: orderForm.specialNotes || '',
    });

    setCheckoutDialogOpen(true);
  };

  const handleOrderSubmit = async () => {
    if (vendorsBelowMinimum.length > 0) {
      const message =
        vendorMinimumError || "Please meet each vendor's minimum order amount before placing your order.";
      setOrderError(message);
      return;
    }

    if (
      !orderForm.customerName ||
      !orderForm.customerEmail ||
      !orderForm.customerPhone ||
      !orderForm.deliveryAddress ||
      !orderForm.deliveryDate
    ) {
      setOrderError('Please fill in all required fields');
      return;
    }

    setOrderLoading(true);
    setOrderError(null);

    try {
      if (!user?.phoneNumber) {
        throw new Error('User not authenticated');
      }

      const orders = await orderService.createOrder(cart.items, orderForm, user.phoneNumber);

      if (orders.length > 0) {
        clearCart();
      }

      setOrderForm({
        customerName: '',
        customerEmail: '',
        customerPhone: '',
        deliveryAddress: '',
        deliveryDate: '',
        specialNotes: '',
      });

      setCheckoutDialogOpen(false);
      onClose();

      await new Promise(resolve => setTimeout(resolve, 100));

      if (onCheckout) {
        onCheckout();
      }
    } catch (error: any) {
      console.error('Order creation error:', error);
      let errorMessage = 'Failed to create order(s). Please try again.';
      if (error.message) {
        errorMessage = error.message;
      }
      console.error('Full error object:', error);
      setOrderError(errorMessage);
    } finally {
      setOrderLoading(false);
    }
  };

  const getItemImage = (item: CartItem) => {
    if ((item.type === 'plate' || item.type === 'dish') && item.image) {
      return item.image;
    }
    return '/api/placeholder/80/80';
  };

  const hasItems = cart.items && cart.items.length > 0;

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        BackdropProps={{
          sx: { bgcolor: 'rgba(46, 38, 28, 0.35)' },
        }}
        PaperProps={{
          elevation: 0,
          sx: {
            width: { xs: 'min(100vw, 440px)', sm: 420, md: 448 },
            maxWidth: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: C.threadBg,
            borderLeft: `1px solid ${C.border}`,
            boxShadow: '-12px 0 48px rgba(46, 38, 28, 0.14)',
            backgroundImage: `linear-gradient(185deg, ${C.shellBg} 0%, ${C.threadBg} 22%, ${C.threadBg} 100%)`,
          },
        }}
      >
        {/* Header — Chaavi */}
        <Box
          sx={{
            px: 2.5,
            pt: 2.25,
            pb: 2,
            flexShrink: 0,
            borderBottom: `1px solid ${C.border}`,
            bgcolor: C.shellBg,
          }}
        >
          <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={2}>
            <Box display="flex" alignItems="center" gap={1.5} minWidth={0}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2,
                  bgcolor: C.goldPale,
                  border: `1px solid ${C.border}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <ShoppingBagIcon sx={{ color: C.goldDark, fontSize: 26 }} />
              </Box>
              <Box minWidth={0}>
                <Typography
                  sx={{
                    fontFamily: '"Playfair Display", Georgia, serif',
                    fontWeight: 400,
                    fontSize: '1.4rem',
                    color: C.text,
                    lineHeight: 1.2,
                  }}
                  component="h2"
                >
                  Your cart
                </Typography>
                <Typography
                  sx={{
                    fontFamily: '"Jost", "Roboto", sans-serif',
                    fontSize: '0.8125rem',
                    color: C.textMuted,
                    mt: 0.35,
                  }}
                >
                  {hasItems
                    ? `${cart.totalItems} ${cart.totalItems === 1 ? 'item' : 'items'} ready to order`
                    : 'Nothing here yet'}
                </Typography>
              </Box>
            </Box>
            <IconButton
              onClick={onClose}
              aria-label="Close cart"
              sx={{
                flexShrink: 0,
                border: `1px solid ${C.border}`,
                color: C.goldDark,
                bgcolor: C.shellBg,
                '&:hover': {
                  bgcolor: C.goldMuted,
                  borderColor: C.gold,
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {!hasItems ? (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              px: 3,
              py: 6,
            }}
          >
            <Box
              sx={{
                width: 92,
                height: 92,
                borderRadius: '50%',
                bgcolor: C.emptyCircle,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2.5,
              }}
            >
              <ShoppingBagIcon sx={{ fontSize: 42, color: C.gold }} />
            </Box>
            <Typography
              sx={{
                fontFamily: '"Playfair Display", serif',
                fontWeight: 400,
                fontSize: '1.15rem',
                color: C.text,
                mb: 1,
              }}
            >
              Your cart is empty
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Jost", sans-serif',
                fontSize: '0.875rem',
                color: C.textMuted,
                textAlign: 'center',
                maxWidth: 260,
              }}
            >
              Browse themes, catering, and décor — add what you love.
            </Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0, px: 2, py: 2 }}>
              {(() => {
                const uniqueVendors = new Set(cart.items.map(item => item.businessId));
                const vendorCount = uniqueVendors.size;
                return vendorCount > 1 ? (
                  <Alert
                    severity="info"
                    icon={false}
                    sx={{
                      mb: 2,
                      borderRadius: 2,
                      bgcolor: C.goldMuted,
                      border: `1px solid ${C.border}`,
                      color: C.text,
                      '& .MuiAlert-message': { width: '100%', fontFamily: '"Jost", sans-serif', fontSize: '0.8125rem' },
                    }}
                  >
                    Items from <strong>{vendorCount}</strong> vendors — we&apos;ll create one order per vendor at
                    checkout.
                  </Alert>
                ) : null;
              })()}

              {vendorSummaries.filter(summary => summary.min > 0).length > 0 && (
                <Box sx={{ mb: 2 }}>
                  {vendorSummaries
                    .filter(summary => summary.min > 0)
                    .map(summary => {
                      const meetsMinimum = summary.total + 0.0001 >= summary.min;
                      const shortfall = summary.min - summary.total;
                      return (
                        <Alert
                          key={summary.phone}
                          severity={meetsMinimum ? 'success' : 'warning'}
                          sx={{
                            mb: 1,
                            borderRadius: 2,
                            border: `1px solid ${C.border}`,
                            '& .MuiAlert-message': { fontFamily: '"Jost", sans-serif', fontSize: '0.8125rem' },
                          }}
                        >
                          <strong>{summary.vendorName}:</strong> {formatPrice(summary.total)} / {formatPrice(summary.min)}
                          {!meetsMinimum && <span> · Add {formatPrice(shortfall)} more</span>}
                        </Alert>
                      );
                    })}
                </Box>
              )}

              <List disablePadding>
                {(() => {
                  const sortedItems = [...cart.items].sort((a, b) => {
                    if (a.businessId !== b.businessId) {
                      return a.businessId.localeCompare(b.businessId);
                    }
                    return 0;
                  });

                  return sortedItems.map((item, index) => {
                    const isFirstItemFromVendor =
                      index === 0 || sortedItems[index - 1].businessId !== item.businessId;

                    return (
                      <React.Fragment key={`${item.id}-${item.type}`}>
                        {isFirstItemFromVendor && (
                          <Box
                            sx={{
                              mt: index > 0 ? 2.5 : 0,
                              mb: 1.5,
                              pl: 1.25,
                              py: 0.75,
                              borderLeft: `3px solid ${C.gold}`,
                              bgcolor: C.goldMuted,
                              borderRadius: '0 8px 8px 0',
                            }}
                          >
                            <Typography
                              sx={{
                                fontFamily: '"Jost", sans-serif',
                                fontSize: '0.65rem',
                                letterSpacing: '0.14em',
                                textTransform: 'uppercase',
                                color: C.goldDark,
                                fontWeight: 600,
                              }}
                            >
                              {item.businessName}
                            </Typography>
                          </Box>
                        )}
                        <ListItem disableGutters sx={{ display: 'block', mb: 2, p: 0 }}>
                          <Paper
                            elevation={0}
                            sx={{
                              p: 2,
                              borderRadius: 3,
                              border: `1px solid ${C.border}`,
                              bgcolor: C.shellBg,
                              boxShadow: '0 2px 14px rgba(46, 38, 28, 0.06)',
                            }}
                          >
                            <Box display="flex" gap={2} width="100%">
                              <Avatar
                                variant="rounded"
                                src={getItemImage(item)}
                                sx={{
                                  width: 72,
                                  height: 72,
                                  flexShrink: 0,
                                  borderRadius: 2,
                                  border: `1px solid ${C.border}`,
                                  bgcolor: C.threadBg,
                                }}
                              />
                              <Box flex={1} minWidth={0}>
                                <Typography
                                  sx={{
                                    fontFamily: '"Playfair Display", serif',
                                    fontWeight: 500,
                                    fontSize: '1.02rem',
                                    color: C.text,
                                    lineHeight: 1.3,
                                    mb: 0.5,
                                  }}
                                >
                                  {item.name}
                                </Typography>
                                {!isFirstItemFromVendor && (
                                  <Typography
                                    variant="caption"
                                    sx={{ color: C.textMuted, fontFamily: '"Jost", sans-serif', display: 'block', mb: 0.5 }}
                                  >
                                    {item.businessName}
                                  </Typography>
                                )}
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: C.textMuted,
                                    fontFamily: '"Jost", sans-serif',
                                    fontSize: '0.8rem',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    mb: 1,
                                  }}
                                >
                                  {item.description}
                                </Typography>

                                {item.type === 'plate' && item.selectedDishes && item.selectedDishes.length > 0 && (
                                  <Box
                                    sx={{
                                      mb: 1.25,
                                      pl: 1.25,
                                      borderLeft: `2px solid ${C.gold}`,
                                      py: 0.5,
                                    }}
                                  >
                                    <Typography
                                      variant="caption"
                                      sx={{ fontWeight: 700, color: C.goldDark, fontFamily: '"Jost", sans-serif' }}
                                    >
                                      Selected dishes
                                    </Typography>
                                    {item.selectedDishes.map((dish, idx) => (
                                      <Typography
                                        key={idx}
                                        variant="caption"
                                        display="block"
                                        sx={{ color: C.textMuted, fontFamily: '"Jost", sans-serif' }}
                                      >
                                        · {dish.dishName} ×{dish.quantity} — {formatPrice(dish.dishPrice * dish.quantity)}
                                      </Typography>
                                    ))}
                                  </Box>
                                )}

                                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={1.25}>
                                  <Chip
                                    label={item.type}
                                    size="small"
                                    sx={{
                                      height: 24,
                                      fontSize: '0.65rem',
                                      textTransform: 'capitalize',
                                      border: `1px solid ${C.border}`,
                                      color: C.goldDark,
                                      bgcolor: 'transparent',
                                      fontFamily: '"Jost", sans-serif',
                                      fontWeight: 500,
                                    }}
                                  />
                                  <Typography
                                    sx={{
                                      fontFamily: '"Jost", sans-serif',
                                      fontWeight: 600,
                                      color: C.goldDark,
                                      fontSize: '0.9rem',
                                    }}
                                  >
                                    {formatPrice(item.price)} <Box component="span" sx={{ fontWeight: 400, color: C.textMuted }}>each</Box>
                                  </Typography>
                                </Box>

                                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<CalendarIcon sx={{ fontSize: 18 }} />}
                                    onClick={() => handleDatePickerOpen(item.id, item.type)}
                                    sx={{
                                      textTransform: 'none',
                                      borderRadius: 2,
                                      borderColor: C.border,
                                      color: C.text,
                                      fontFamily: '"Jost", sans-serif',
                                      fontSize: '0.75rem',
                                      '&:hover': { borderColor: C.gold, bgcolor: C.goldMuted },
                                    }}
                                  >
                                    {item.bookingDate
                                      ? format(new Date(item.bookingDate), 'MMM d, yyyy')
                                      : 'Booking date'}
                                  </Button>
                                  {item.bookingDate && (
                                    <Button
                                      size="small"
                                      onClick={() => updateBookingDate(item.id, item.type, undefined)}
                                      sx={{ textTransform: 'none', fontFamily: '"Jost", sans-serif', fontSize: '0.75rem', color: C.goldDark }}
                                    >
                                      Clear
                                    </Button>
                                  )}
                                </Box>
                              </Box>
                            </Box>

                            <Box
                              display="flex"
                              alignItems="center"
                              justifyContent="space-between"
                              flexWrap="wrap"
                              gap={2}
                              mt={2}
                              pt={2}
                              borderTop={`1px solid ${C.border}`}
                            >
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="caption" sx={{ color: C.textMuted, fontFamily: '"Jost", sans-serif', mr: 0.5 }}>
                                  Qty
                                </Typography>
                                <IconButton
                                  size="small"
                                  onClick={() => handleQuantityChange(item.id, item.type, item.quantity - 1)}
                                  sx={{
                                    border: `1px solid ${C.border}`,
                                    width: 34,
                                    height: 34,
                                    color: C.goldDark,
                                    '&:hover': { bgcolor: C.goldMuted },
                                  }}
                                >
                                  <RemoveIcon fontSize="small" />
                                </IconButton>
                                <Typography
                                  sx={{
                                    minWidth: 28,
                                    textAlign: 'center',
                                    fontWeight: 700,
                                    fontFamily: '"Jost", sans-serif',
                                    color: C.text,
                                  }}
                                >
                                  {item.quantity}
                                </Typography>
                                <IconButton
                                  size="small"
                                  onClick={() => handleQuantityChange(item.id, item.type, item.quantity + 1)}
                                  sx={{
                                    border: `1px solid ${C.border}`,
                                    width: 34,
                                    height: 34,
                                    color: C.goldDark,
                                    '&:hover': { bgcolor: C.goldMuted },
                                  }}
                                >
                                  <AddIcon fontSize="small" />
                                </IconButton>
                              </Box>

                              <Box display="flex" alignItems="center" gap={1.5}>
                                <Box textAlign="right">
                                  <Typography variant="caption" sx={{ color: C.textMuted, fontFamily: '"Jost", sans-serif' }}>
                                    Subtotal
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontWeight: 700,
                                      fontFamily: '"Jost", sans-serif',
                                      color: C.goldDark,
                                      fontSize: '1rem',
                                      display: 'block',
                                    }}
                                  >
                                    {formatPrice(item.price * item.quantity)}
                                  </Typography>
                                </Box>
                                <IconButton
                                  size="small"
                                  onClick={() => removeFromCart(item.id, item.type)}
                                  aria-label="Remove item"
                                  sx={{
                                    border: `1px solid ${C.border}`,
                                    color: C.textMuted,
                                    '&:hover': { color: 'error.main', bgcolor: 'rgba(211, 47, 47, 0.06)', borderColor: 'error.light' },
                                  }}
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </Box>
                          </Paper>
                        </ListItem>
                      </React.Fragment>
                    );
                  });
                })()}
              </List>
            </Box>

            <Box
              sx={{
                flexShrink: 0,
                px: 2.5,
                py: 2,
                bgcolor: C.shellBg,
                borderTop: `1px solid ${C.border}`,
                boxShadow: '0 -8px 24px rgba(46, 38, 28, 0.06)',
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="baseline" mb={0.5}>
                <Typography sx={{ fontFamily: '"Jost", sans-serif', color: C.textMuted, fontSize: '0.875rem' }}>
                  Total ({cart.totalItems} items)
                </Typography>
                <Typography
                  sx={{
                    fontFamily: '"Playfair Display", serif',
                    fontSize: '1.35rem',
                    fontWeight: 500,
                    color: C.goldDark,
                  }}
                >
                  {formatPrice(cart.totalPrice)}
                </Typography>
              </Box>
              {!canProceedToCheckout && vendorMinimumError && (
                <Typography variant="caption" sx={{ color: 'error.main', display: 'block', mb: 1, fontFamily: '"Jost", sans-serif' }}>
                  {vendorMinimumError}
                </Typography>
              )}
              <Button
                fullWidth
                variant="contained"
                disableElevation
                size="large"
                startIcon={<CheckoutIcon />}
                onClick={handleCheckout}
                disabled={!canProceedToCheckout}
                sx={{
                  mt: 1.5,
                  py: 1.4,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontFamily: '"Jost", sans-serif',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  letterSpacing: '0.03em',
                  background: `linear-gradient(135deg, #d4a84a 0%, #b8860b 100%)`,
                  color: '#1a1208',
                  boxShadow: '0 8px 24px rgba(184, 134, 11, 0.35)',
                  '&:hover': {
                    background: `linear-gradient(135deg, #e0b855 0%, #c9a227 100%)`,
                    boxShadow: '0 10px 28px rgba(184, 134, 11, 0.42)',
                  },
                  '&.Mui-disabled': {
                    background: 'rgba(46, 38, 28, 0.12)',
                    color: 'rgba(46, 38, 28, 0.38)',
                    boxShadow: 'none',
                  },
                }}
              >
                Proceed to checkout
              </Button>
            </Box>
          </>
        )}
      </Drawer>

      <Dialog
        open={checkoutDialogOpen}
        onClose={() => {
          setCheckoutDialogOpen(false);
          setOrderError(null);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: `1px solid ${C.border}`,
            bgcolor: C.shellBg,
            backgroundImage: `linear-gradient(180deg, ${C.shellBg} 0%, ${C.threadBg} 100%)`,
          },
        }}
      >
        <DialogTitle>
          <Typography sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 400, fontSize: '1.35rem', color: C.text }}>
            Complete your order
          </Typography>
          <Typography sx={{ fontFamily: '"Jost", sans-serif', fontSize: '0.8125rem', color: C.textMuted, mt: 0.5, fontWeight: 400 }}>
            We&apos;ll create separate orders per vendor when needed.
          </Typography>
        </DialogTitle>
        <DialogContent>
          {orderError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {orderError}
            </Alert>
          )}

          <Box sx={{ pt: 0.5 }}>
            <TextField
              fullWidth
              label="Customer Name"
              value={orderForm.customerName}
              onChange={e => setOrderForm({ ...orderForm, customerName: e.target.value })}
              margin="normal"
              required
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, fontFamily: '"Jost", sans-serif' } }}
            />
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={orderForm.customerEmail}
              onChange={e => setOrderForm({ ...orderForm, customerEmail: e.target.value })}
              margin="normal"
              required
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, fontFamily: '"Jost", sans-serif' } }}
            />
            <TextField
              fullWidth
              label="Phone Number"
              value={orderForm.customerPhone}
              onChange={e => setOrderForm({ ...orderForm, customerPhone: e.target.value })}
              margin="normal"
              required
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, fontFamily: '"Jost", sans-serif' } }}
            />
            <TextField
              fullWidth
              label="Delivery Address"
              value={orderForm.deliveryAddress}
              onChange={e => setOrderForm({ ...orderForm, deliveryAddress: e.target.value })}
              margin="normal"
              required
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, fontFamily: '"Jost", sans-serif' } }}
            />
            <TextField
              fullWidth
              label="Delivery Date"
              type="date"
              value={orderForm.deliveryDate}
              onChange={e => setOrderForm({ ...orderForm, deliveryDate: e.target.value })}
              margin="normal"
              InputLabelProps={{ shrink: true }}
              required
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, fontFamily: '"Jost", sans-serif' } }}
            />
            <TextField
              fullWidth
              label="Special Notes (Optional)"
              multiline
              rows={3}
              value={orderForm.specialNotes}
              onChange={e => setOrderForm({ ...orderForm, specialNotes: e.target.value })}
              margin="normal"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, fontFamily: '"Jost", sans-serif' } }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => setCheckoutDialogOpen(false)}
            sx={{ textTransform: 'none', fontFamily: '"Jost", sans-serif', color: C.textMuted }}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              handleOrderSubmit();
            }}
            variant="contained"
            disableElevation
            disabled={orderLoading}
            startIcon={orderLoading ? <CircularProgress size={20} color="inherit" /> : <CheckoutIcon />}
            sx={{
              textTransform: 'none',
              fontFamily: '"Jost", sans-serif',
              fontWeight: 600,
              borderRadius: 2,
              px: 2.5,
              background: `linear-gradient(135deg, #d4a84a 0%, #b8860b 100%)`,
              color: '#1a1208',
              '&:hover': { background: `linear-gradient(135deg, #e0b855 0%, #c9a227 100%)` },
            }}
          >
            {orderLoading ? 'Creating orders…' : 'Place order(s)'}
          </Button>
        </DialogActions>
      </Dialog>

      {selectedItemForDate && (
        <DatePickerDialog
          open={datePickerOpen}
          onClose={() => {
            setDatePickerOpen(false);
            setSelectedItemForDate(null);
          }}
          onConfirm={handleDateConfirm}
          itemId={selectedItemForDate.id}
          itemType={selectedItemForDate.type}
          currentDate={cart.items.find(
            item => item.id === selectedItemForDate.id && item.type === selectedItemForDate.type
          )?.bookingDate}
          title="Select Booking Date"
        />
      )}
    </>
  );
};

export default Cart;
